import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Container, VStack, Heading, Text, Box, Badge, HStack, Image,
  OrderedList, ListItem, Divider, Button, Link,
} from "@chakra-ui/react";
import { FiArrowLeft } from "react-icons/fi";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";

const LMAttachmentUpgrade: React.FC = () => {
  return (
    <>
      <NavbarWithCallToAction />
      <Container maxW="container.md" py={10}>
        <VStack spacing={6} align="stretch">
          <Link as={RouterLink} to="/updates" _hover={{ textDecoration: "none" }}>
            <Button variant="ghost" size="sm" leftIcon={<FiArrowLeft />} color="gray.500">
              All Updates
            </Button>
          </Link>

          <VStack spacing={3} align="start">
            <HStack>
              <Badge colorScheme="blue">SurgReady</Badge>
              <Badge colorScheme="green">Learning Resources</Badge>
              <Badge colorScheme="purple">Admin UX</Badge>
              <Text fontSize="xs" color="gray.400">22 Apr 2026</Text>
            </HStack>
            <Heading size="lg" color="#2c5282">
              Richer file previews + hover-help across the course builder
            </Heading>
            <Text color="gray.600">
              Two improvements shipped together. Every lesson attachment (PDFs, worksheets, screening tools) now renders as a branded card with three affordances — preview, open, download. And every admin page in the course builder now has hover-help on its buttons and form fields so building courses feels self-explanatory.
            </Text>
          </VStack>

          <Box>
            <Image
              src="/images/lm-attachment-card.png"
              alt="The new Lesson Resources card — file-type icon, filename, size and type badges, and three action buttons (Preview, Open, Download). Appears below the lesson content."
              borderRadius="md"
              boxShadow="0 4px 16px rgba(0,0,0,0.1)"
              border="1px solid"
              borderColor="gray.200"
              fallbackSrc="https://via.placeholder.com/900x360?text=Lesson+Resources+card+preview"
            />
            <Text fontSize="sm" color="gray.500" fontStyle="italic" textAlign="center" mt={2}>
              What it looks like below the lesson.
            </Text>
          </Box>

          <Box>
            <Image
              src="/images/lm-attachment-inline-preview.png"
              alt="The inline PDF preview modal open on the weekly menu PDF — full readable content, Open in new tab and Download buttons in the header, close button top-right."
              borderRadius="md"
              boxShadow="0 4px 16px rgba(0,0,0,0.1)"
              border="1px solid"
              borderColor="gray.200"
              fallbackSrc="https://via.placeholder.com/900x600?text=Inline+PDF+preview+modal"
            />
            <Text fontSize="sm" color="gray.500" fontStyle="italic" textAlign="center" mt={2}>
              Full-screen inline preview when Preview is clicked.
            </Text>
          </Box>

          <Divider />

          <Box>
            <Heading size="md" color="#2b6cb0" mb={3}>What's New (student side)</Heading>
            <VStack spacing={3} align="start">
              <Text>
                <strong>Three affordances on every file.</strong> Each attachment on a lesson is now rendered as a card with a file-type icon, filename, size badge, and three buttons. Students can <strong>Preview</strong> a PDF inline without leaving the lesson, <strong>Open</strong> it in a new tab for a clean full-screen read, or <strong>Download</strong> to keep.
              </Text>
              <Text>
                <strong>File-type icons at a glance.</strong> PDFs show a red icon, Word is blue, Excel green, PowerPoint orange, archives grey, images purple. Scannable at speed when a lesson has multiple resources.
              </Text>
              <Text>
                <strong>Inline PDF viewer.</strong> Clicking Preview opens a modal with the PDF rendered in place. Page navigation at the bottom for multi-page files. A Pop-out button in the header moves it to a new tab if the reader prefers the full-screen experience. Download button also in the header.
              </Text>
              <Text>
                <strong>Mobile-first layout.</strong> Cards sit in two columns on desktop, stack to one on mobile. Action buttons wrap under the metadata on narrow screens.
              </Text>
              <Text>
                <strong>A subtle fade-up.</strong> Cards animate in on first render so students notice each resource is clickable — gentle, not needy.
              </Text>
            </VStack>
          </Box>

          <Box>
            <Heading size="md" color="#2b6cb0" mb={3}>What's New (admin side)</Heading>
            <VStack spacing={3} align="start">
              <Text>
                <strong>Hover-help across the course builder.</strong> Every button, form field, tab, and icon in the admin pages now has a short hover explanation of what it does and what happens when you click it. No more guessing. New admin staff can find their way around in minutes.
              </Text>
              <Text>
                <strong>Intro banners on the editor pages.</strong> Course Editor, Module Editor, and Lesson Editor each have a one-line explanation at the top — what the page is for, what the tabs do, what Preview shows.
              </Text>
            </VStack>
          </Box>

          <Box>
            <Heading size="md" color="#2b6cb0" mb={3}>How It Works</Heading>
            <OrderedList spacing={3} pl={4}>
              <ListItem><strong>Admin</strong> — open a lesson, click the Attachments tab, upload a PDF or document. (Supports PDF, Word, Excel, PowerPoint, text, ZIP.)</ListItem>
              <ListItem><strong>Student</strong> — opens the lesson and sees the resource as a branded card at the bottom.</ListItem>
              <ListItem><strong>Preview</strong> opens a PDF inline in a modal. Page controls at the bottom, Pop-out and Download in the header.</ListItem>
              <ListItem><strong>Open</strong> loads the file in a new browser tab.</ListItem>
              <ListItem><strong>Download</strong> saves the file to the device.</ListItem>
            </OrderedList>
          </Box>

          <Box>
            <Heading size="md" color="#2b6cb0" mb={3}>Why It Matters</Heading>
            <Text>
              Students decide how to engage with each resource on their terms — skim, read, or save. Admins build courses with confidence because every control explains itself on hover. Both ends of the journey feel considered.
            </Text>
          </Box>

          <Box>
            <Text>
              As always, reach out if you have any questions, requests, or new ideas.
            </Text>
            <Text mt={4}>
              Regards,<br />
              <strong>Dr Thomas Miller</strong><br />
              <Text as="em" color="gray.500">Tom Miller Services · Business Builder System</Text>
            </Text>
          </Box>
        </VStack>
      </Container>
      <FooterWithFourColumns />
    </>
  );
};

export default LMAttachmentUpgrade;
